import { useMemo } from "react";
import { useLocalization } from "@/context/localization-context";
import { EmptyBlock, SectionCard } from "../../components/primitives";
import { TimeSeriesChart, type ChartSeries } from "../../components/time-series-chart";
import { formatCount, formatMb, formatMs, formatMsTick } from "../../format";
import { SERIES, STATUS } from "../../palette";
import type { MetricSeries } from "../../types";
import { LIMITS } from "../backend/tone";
import { hasMax, maxField, podBeyondPalette, podColor } from "./pod-order";

const GAUGES = ["gpu_util_pct", "gpu_memory_mb", "gpu_temperature_c", "gpu_power_w", "loop_lag_ms"] as const;
type Gauge = (typeof GAUGES)[number];

/** memory is charted in GB, so the axis gets round GB ticks */
const SCALE: Partial<Record<Gauge, number>> = { gpu_memory_mb: 1 / 1024 };

const unit = (suffix: string) => (value: number) => `${formatCount(value)} ${suffix}`;
const formatPct = unit("%");
const formatCelsius = unit("°C");
const formatWatts = unit("W");
const formatGbTick = unit("GB");
const formatGb = (value: number) => formatMb(value * 1024);

/**
 * The window history of the pods' own GPU and event loop (per-minute maxes the backend records from each
 * pod's GET /metrics), one line per pod in the tab's pod colours.
 */
export function PodGpuCharts({ metrics, order }: { metrics: MetricSeries; order: string[] }) {
  const { t } = useLocalization();

  const charts = useMemo(() => {
    // series keys without dots: recharts reads "a.b" as a nested path
    const perPod = (gauge: Gauge): ChartSeries[] =>
      order
        .map((endpoint, index) => ({ endpoint, index }))
        .filter(({ endpoint }) => hasMax(metrics, `pod.${gauge}@${endpoint}`))
        .map(({ endpoint, index }) => ({
          key: `pod${index}`,
          label: endpoint,
          color: podColor(index),
          values: maxField(metrics, `pod.${gauge}@${endpoint}`).map((v) => (v === null ? null : v * (SCALE[gauge] ?? 1))),
          type: "line" as const,
          dashed: podBeyondPalette(index),
          hidden: podBeyondPalette(index),
        }));
    return Object.fromEntries(GAUGES.map((gauge) => [gauge, perPod(gauge)])) as Record<Gauge, ChartSeries[]>;
  }, [metrics, order]);

  if (GAUGES.every((gauge) => charts[gauge].length === 0)) {
    return <EmptyBlock>{t("monitoring.pods.chart.noGpu")}</EmptyBlock>;
  }

  const common = { t: metrics.t, stepMs: metrics.step_ms, window: metrics.window };
  const extraPods = order.length > SERIES.length ? ` ${t("monitoring.pods.chart.extraPods")}` : "";
  // remount when the set of pods changes (the legend's hidden state is per series key)
  const chartKey = (gauge: Gauge) => charts[gauge].map((s) => s.label).join("|");
  const empty = t("monitoring.noData");

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      <SectionCard
        title={t("monitoring.pods.chart.gpuUtil")}
        description={`${t("monitoring.pods.chart.gpuUtilDescription")}${extraPods}`}
      >
        <TimeSeriesChart
          key={chartKey("gpu_util_pct")}
          {...common}
          series={charts.gpu_util_pct}
          yMax={100}
          format={formatPct}
          tickFormat={formatPct}
          zeroIsData
          emptyText={empty}
        />
      </SectionCard>
      <SectionCard title={t("monitoring.pods.chart.gpuMemory")} description={t("monitoring.pods.chart.gpuMemoryDescription")}>
        <TimeSeriesChart
          key={chartKey("gpu_memory_mb")}
          {...common}
          series={charts.gpu_memory_mb}
          format={formatGb}
          tickFormat={formatGbTick}
          emptyText={empty}
        />
      </SectionCard>
      <SectionCard
        title={t("monitoring.pods.chart.gpuTemperature")}
        description={t("monitoring.pods.chart.gpuTemperatureDescription", { hot: LIMITS.podGpuHotC })}
      >
        <TimeSeriesChart
          key={chartKey("gpu_temperature_c")}
          {...common}
          series={charts.gpu_temperature_c}
          yMax={100}
          format={formatCelsius}
          tickFormat={formatCelsius}
          referenceLines={[{ y: LIMITS.podGpuHotC, label: t("monitoring.pods.chart.hot"), color: STATUS.warning }]}
          emptyText={empty}
        />
      </SectionCard>
      <SectionCard title={t("monitoring.pods.chart.gpuPower")} description={t("monitoring.pods.chart.gpuPowerDescription")}>
        <TimeSeriesChart
          key={chartKey("gpu_power_w")}
          {...common}
          series={charts.gpu_power_w}
          format={formatWatts}
          tickFormat={formatWatts}
          emptyText={empty}
        />
      </SectionCard>
      <SectionCard
        className="xl:col-span-2"
        title={t("monitoring.pods.chart.podLoop")}
        description={t("monitoring.pods.chart.podLoopDescription")}
      >
        <TimeSeriesChart
          key={chartKey("loop_lag_ms")}
          {...common}
          series={charts.loop_lag_ms}
          height={170}
          format={formatMs}
          tickFormat={formatMsTick}
          referenceLines={[{ y: LIMITS.podLoopLagMs, label: t("monitoring.backend.loop.nokLine"), color: STATUS.warning }]}
          emptyText={empty}
        />
      </SectionCard>
    </div>
  );
}
